import { v4 as uuidv4 } from 'uuid';
import {
  MessageType,
  ImageType,
  PdfFileType,
  TsCssVariable,
  MessageRoleType,
  AiResponseIssueKey,
  AiResponseResponseType,
  viewType,
  ActiveViewEmbedType,
} from 'types';
import { filterApiMessages, extractJsonFromReadme, getIsManualModeActive } from 'utils/aiChat';
import { getClusterVersion, getSdkVersion } from 'utils/version';
import { EMBED_FEATURE_FLAGS_BY_VIEW } from '../../constants';

const API_CONFIG = {
  METHOD: 'POST',
  PLAYGROUND_TYPE: 'theme-builder',
  HEADERS: { 'Content-Type': 'application/json' },
  ENDPOINT: process.env.REACT_APP_AI_MODE_ENDPOINT || '',
};

export const handleThemeBuilderAiModeRequest = async ({
  userInput,
  chatMessages,
  isStreaming,
  setIsStreaming,
  abortControllerRef,
  resetStyleState,
  updateLastAiMessage,
  styleState,
  activeView,
}: {
  userInput: { text: string; images?: ImageType[]; pdfs?: PdfFileType[] };
  chatMessages: MessageType[];
  isStreaming: boolean;
  setIsStreaming: (value: boolean) => void;
  abortControllerRef: any;
  resetStyleState: (state: Partial<Record<TsCssVariable, string>>) => void;
  updateLastAiMessage: (message: string, id: MessageRoleType, isManualModeActive?: boolean) => void;
  styleState: Partial<Record<TsCssVariable, string>>;
  activeView: viewType;
}) => {
  const trimmed = userInput.text.trim();
  const hasContent = trimmed || userInput?.images || userInput?.pdfs;
  if (!hasContent || isStreaming) return;

  const userMessage: MessageType = {
    role: MessageRoleType.USER,
    content: trimmed,
    images: userInput.images,
    pdfFiles: userInput.pdfs,
    id: `user-${uuidv4()}`,
  };
  const updatedMessages = filterApiMessages(chatMessages, styleState, userMessage);

  abortControllerRef.current = new AbortController();
  try {
    const apiMessages = updatedMessages.map((m) => {
      if (m.role !== MessageRoleType.USER) {
        return { role: 'assistant', content: m.content };
      }

      const hasImages = m.images;
      const hasPdfFiles = m.pdfFiles;

      if (hasImages || hasPdfFiles) {
        const contentParts: Array<
          { type: 'text'; text: string } | { type: 'image'; image: string }
        > = [];
        if (m.content) {
          contentParts.push({ type: 'text', text: m.content });
        }

        const pdfFiles = m.pdfFiles?.map((pdf) => ({ name: pdf.name, text: pdf.text })) || [];
        for (const pdf of pdfFiles) {
          contentParts.push({ type: 'text', text: `[PDF: ${pdf.name}]\n\n${pdf.text}` });
        }

        const images = m.images?.map((img) => img.content) || [];
        for (const image of images) {
          contentParts.push({ type: 'image', image: image });
        }
        return { role: MessageRoleType.USER, content: contentParts };
      }

      return { role: MessageRoleType.USER, content: m.content };
    });

    const clusterVersion = await getClusterVersion();
    const sdkVersion = getSdkVersion();

    const response = await fetch(API_CONFIG.ENDPOINT, {
      method: API_CONFIG.METHOD,
      headers: API_CONFIG.HEADERS,
      body: JSON.stringify({
        playgroundType: API_CONFIG.PLAYGROUND_TYPE,
        messages: apiMessages,
        embedType: ActiveViewEmbedType[activeView],
        environment: {
          clusterVersion,
          sdkVersion,
          flags: EMBED_FEATURE_FLAGS_BY_VIEW[activeView],
        },
      }),
      signal: abortControllerRef.current.signal,
    });

    if (!response.ok) {
      updateLastAiMessage(AiResponseIssueKey.NETWORK_ISSUE, MessageRoleType.ISSUE);
      return;
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    if (!reader) throw new Error('No reader available');

    let buffer = '';
    let accumulated = '';

    while (true as boolean) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      const filteredLines = lines.filter((line) => line.trim() !== '');

      let isDone = false;
      for (const line of filteredLines) {
        const jsonStr = line.startsWith('data: ') ? line.slice(6) : line;
        try {
          const json = JSON.parse(jsonStr);
          switch (json.type) {
            case AiResponseResponseType.TEXT:
              if (json.content) {
                accumulated += json.content;
                updateLastAiMessage(accumulated, MessageRoleType.AI);
              }
              break;
            case AiResponseResponseType.DONE: {
              const newStyleState = extractJsonFromReadme(accumulated);
              if (newStyleState) {
                resetStyleState(newStyleState);
                updateLastAiMessage(accumulated, MessageRoleType.AI);
              } else {
                const isManualModeActiveValue = getIsManualModeActive(chatMessages);
                updateLastAiMessage(accumulated, MessageRoleType.AI, isManualModeActiveValue);
              }
              isDone = true;
              break;
            }
            case AiResponseResponseType.ERROR:
              if (json.content) {
                updateLastAiMessage(AiResponseIssueKey.PROCESSING_ISSUE, MessageRoleType.ISSUE);
                isDone = true;
              }
              break;
            default:
              break;
          }
        } catch (error) {
          updateLastAiMessage(AiResponseIssueKey.PROCESSING_ISSUE, MessageRoleType.ISSUE);
          isDone = true;
        }
      }
      if (isDone) break;
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      updateLastAiMessage(AiResponseIssueKey.USER_CANCELLED_ISSUE, MessageRoleType.ISSUE);
    } else {
      updateLastAiMessage(AiResponseIssueKey.INTERNAL_SERVER_ISSUE, MessageRoleType.ISSUE);
    }
  } finally {
    setIsStreaming(false);
    abortControllerRef.current = null;
  }
};
